import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  RocketLaunch,
  Star,
  TrendingUp,
  CheckCircle,
  CompareArrows
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// Default credit packages (moved inside the component file)
const DEFAULT_CREDIT_PACKAGES = [
  { id: 'basic', credits: 5, inrPrice: 1000, bonus: 0, popular: false },
  { id: 'pro', credits: 15, inrPrice: 2500, bonus: 3, popular: true },
  { id: 'enterprise', credits: 30, inrPrice: 4500, bonus: 10, popular: false }
];

const PackageCard = ({ 
  package: pkg, 
  currencySymbol,
  convertedPrice,
  pricePerCredit,
  isSelected,
  onSelect,
  index 
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const getPackageColor = () => {
    switch(pkg.id) {
      case 'pro': return theme.palette.primary.main;
      case 'enterprise': return theme.palette.secondary.main;
      default: return theme.palette.info.main;
    }
  };

  const getPackageIcon = () => {
    switch(pkg.id) {
      case 'pro': return <TrendingUp />;
      case 'enterprise': return <RocketLaunch />;
      default: return <TrendingUp />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
    >
      <Card
        sx={{
          height: '100%',
          border: isSelected ? 3 : isHovered ? 2 : 1,
          borderColor: isSelected ? getPackageColor() : 
                     isHovered ? 'primary.light' : 'divider',
          borderRadius: 4,
          overflow: 'visible',
          background: isSelected 
            ? `linear-gradient(135deg, ${getPackageColor()}15 0%, transparent 100%)`
            : 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          transition: 'all 0.3s ease',
          boxShadow: isSelected 
            ? `0 8px 32px ${getPackageColor()}40`
            : '0 4px 20px rgba(0,0,0,0.1)'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Popular Badge */}
        {pkg.popular && (
          <Chip
            icon={<Star />}
            label="MOST POPULAR"
            color="primary"
            size="small"
            sx={{
              position: 'absolute',
              top: -12,
              left: '50%',
              transform: 'translateX(-50%)',
              fontWeight: 'bold',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              color: 'white',
              zIndex: 1
            }}
          />
        )}

        {/* Selection Indicator */}
        {isSelected && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'success.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 2s infinite'
            }}
          >
            <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
          </Box>
        )}

        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          {/* Package Icon */}
          <motion.div
            animate={{ rotate: isHovered ? 360 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: `${getPackageColor()}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: `2px solid ${getPackageColor()}40`
              }}
            >
              {React.cloneElement(getPackageIcon(), {
                sx: { fontSize: 30, color: getPackageColor() }
              })}
            </Box>
          </motion.div>

          {/* Package Name */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {pkg.id.toUpperCase()} PACKAGE
          </Typography>

          {/* Credit Amount */}
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
            {pkg.credits + pkg.bonus}
            <Typography variant="h6" component="span" color="text.secondary">
              {' '}Credits
            </Typography>
          </Typography>

          {/* Price */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: getPackageColor() }}>
              {currencySymbol}{convertedPrice}
            </Typography>
            {pkg.bonus > 0 && (
              <Typography variant="caption" color="success.main" sx={{ fontStyle: 'italic' }}>
                + {pkg.bonus} bonus credits!
              </Typography>
            )}
          </Box>

          {/* Price per Credit */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {currencySymbol}{pricePerCredit.toFixed(2)} per credit
          </Typography>

          {/* Features */}
          <Box sx={{ mb: 3 }}>
            {getPackageFeatures(pkg.id).map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">{feature}</Typography>
                </Box>
              </motion.div>
            ))}
          </Box>

          {/* Select Button */}
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant={isSelected ? "contained" : "outlined"}
              color={pkg.id === 'pro' ? 'primary' : pkg.id === 'enterprise' ? 'secondary' : 'info'}
              fullWidth
              onClick={() => onSelect(pkg)}
              sx={{
                borderRadius: 3,
                py: 1.5,
                fontWeight: 'bold',
                background: isSelected 
                  ? `linear-gradient(45deg, ${getPackageColor()}, ${getPackageColor()}80)`
                  : 'transparent'
              }}
            >
              {isSelected ? 'SELECTED' : 'SELECT PACKAGE'}
            </Button>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const getPackageFeatures = (packageId) => {
  const features = {
    basic: [
      '5 analysis credits',
      'Basic scenario saving',
      'Standard support',
      '30-day validity'
    ],
    pro: [
      '15 analysis credits',
      'Priority scenario processing',
      'Advanced analytics',
      'Email support',
      '90-day validity'
    ],
    enterprise: [
      '30 analysis credits',
      'Unlimited scenario storage',
      'Premium analytics dashboard',
      '24/7 dedicated support',
      '1-year validity',
      'Team collaboration features'
    ]
  };
  return features[packageId] || features.basic;
};

const PackageSelector = ({ 
  onPackageSelect, 
  initialPackage = null,
  creditPackages = DEFAULT_CREDIT_PACKAGES, // Default value added here
  selectedCurrency = 'INR', // Default currency
  currencySymbol = '₹', // Default symbol
  convertPrice = (price) => price // Default conversion function
}) => {
  const theme = useTheme();
  const [selectedPackage, setSelectedPackage] = useState(initialPackage);
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePackageSelect = async (pkg) => {
    setIsProcessing(true);
    
    // Simulate processing delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setSelectedPackage(pkg);
    setIsProcessing(false);
    
    if (onPackageSelect) {
      const convertedPrice = convertPrice(pkg.inrPrice);
      onPackageSelect({
        ...pkg,
        convertedPrice,
        currency: selectedCurrency,
        currencySymbol
      });
    }
  };

  const handlePurchase = () => {
    if (selectedPackage && onPackageSelect) {
      const convertedPrice = convertPrice(selectedPackage.inrPrice);
      onPackageSelect({
        ...selectedPackage,
        convertedPrice,
        currency: selectedCurrency,
        currencySymbol
      });
    }
  };

  // Ensure creditPackages is always an array
  const packagesToRender = Array.isArray(creditPackages) ? creditPackages : DEFAULT_CREDIT_PACKAGES;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            <RocketLaunch sx={{ verticalAlign: 'middle', mr: 1 }} />
            Choose Your Quantum Energy Package
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Select the perfect energy credit package to power your PLM analysis. 
            More credits = more scenarios = more insights!
          </Typography>
        </motion.div>
      </Box>

      {/* Packages Grid */}
      {isProcessing && (
        <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />
      )}

      <Grid container spacing={3}>
        {packagesToRender.map((pkg, index) => {
          const convertedPrice = convertPrice(pkg.inrPrice);
          const totalCredits = pkg.credits + pkg.bonus;
          const pricePerCredit = convertedPrice / totalCredits;

          return (
            <Grid item xs={12} md={4} key={pkg.id}>
              <PackageCard
                package={pkg}
                currencySymbol={currencySymbol}
                convertedPrice={convertedPrice}
                pricePerCredit={pricePerCredit}
                isSelected={selectedPackage?.id === pkg.id}
                onSelect={handlePackageSelect}
                index={index}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Selected Package Summary */}
      <AnimatePresence>
        {selectedPackage && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 3,
                bgcolor: 'success.50',
                border: '2px solid',
                borderColor: 'success.200',
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" gutterBottom color="success.main">
                <CheckCircle sx={{ verticalAlign: 'middle', mr: 1 }} />
                Package Selected!
              </Typography>
              <Typography variant="body1" gutterBottom>
                You've chosen the <strong>{selectedPackage.id.toUpperCase()}</strong> package with{' '}
                <strong>{selectedPackage.credits + selectedPackage.bonus} credits</strong>
              </Typography>
              <Typography variant="h5" color="primary" gutterBottom>
                {currencySymbol}{convertPrice(selectedPackage.inrPrice)}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handlePurchase}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                }}
              >
                Proceed to Payment
              </Button>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default PackageSelector;